import type { TimeRange } from "@@/shared/date-time/time-range";
import { sum } from "@@/utilities/sum";
import { isBookableOptionValueWithQuantity } from "../resource-reference-options/bookable-option-value";
import type { BookableSessionId } from "../sessions/bookable-session-id";

import type { ResourceReservationRequest } from "@@/shared/acquire/resources/resource-reservation-request";
import type { ResourceId } from "./resource-id";
import type { ResourceReservation } from "./resource-reservation";
import type { ResourceType } from "./resource-type";

type ResourceReservationRequestInfo = {
    _type: "RESOURCE_RESERVATION_INFO";
    /**
     * Is used to override the time range of the resource reservation request.
     * Solves problems with exact match. Resources are booked from and
     * to a time. But not on the specific time.
     * So this is adjusted to not include the exact start and end time.
     * @type {TimeRange}
     */
    timeRange: TimeRange;
    actualTimeRange: TimeRange;
    resourceId: ResourceId;
    resourceType: ResourceType;
    reservedQuantity: number;
    sessionId?: BookableSessionId;
};

function resourceReservationRequestFactoryInfo(
    resourceReservation: ResourceReservation | ResourceReservationRequest,
    timeRangeOverride: TimeRange,
): ResourceReservationRequestInfo {
    const count = sum(resourceReservation.optionValues, value =>
        isBookableOptionValueWithQuantity(value) ? value.quantity.value : 0,
    );

    return {
        resourceId: resourceReservation.resourceId,
        resourceType: resourceReservation.resourceType,
        timeRange: timeRangeOverride,
        actualTimeRange: resourceReservation.timeRange,
        reservedQuantity:
            resourceReservation.optionValues.length > 0
                ? count
                : resourceReservation.quantity,
        _type: "RESOURCE_RESERVATION_INFO",
        sessionId: resourceReservation.sessionId,
    };
}

export { resourceReservationRequestFactoryInfo };
export type { ResourceReservationRequestInfo };
